import { useInfiniteQuery } from '@tanstack/react-query'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { getNotificationReadReceiptsClient } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { NotificationReadReceiptItemDto } from '@b-stock/notification-api-client'

export const NOTIFICATION_LIST_PAGE_SIZE = 20

export const getNotificationListKey = (personId: string) => [
  'notificationList',
  personId,
]

export const useNotificationList = ({
  enabled,
}: {
  /** Caller-controlled gate (e.g. feature flag AND authenticated). */
  enabled: boolean
}) => {
  const accessToken = useAccessToken()
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId ?? ''

  return useInfiniteQuery({
    queryKey: getNotificationListKey(personId),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await getNotificationReadReceiptsClient().readReceiptsApiControllerFindAll(
          {
            personId,
            limit: NOTIFICATION_LIST_PAGE_SIZE,
            // The generated client mistypes `offset` as `object` (it's
            // documented and behaves as a plain number query param).
            offset: pageParam as unknown as object,
            sortBy: ['createdAt'],
            sortOrder: 'DESC',
          },
          buildAxiosRequestConfig({ accessToken: accessToken ?? '' })
        )
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.receipts.length === 0) return undefined
      const nextOffset = lastPage.offset + lastPage.receipts.length
      return nextOffset < lastPage.total ? nextOffset : undefined
    },
    enabled: enabled && !!accessToken && !!personId,
    // Matches notificationUnreadCountQuery's staleTime: without it, every
    // loaded page refetches in sequence each time the tray is reopened.
    staleTime: 60 * 1000,
  })
}

export type { NotificationReadReceiptItemDto }
